// src/pages/AdminUsersPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Search,
  Pencil,
  Trash2,
  Shield,
  ShieldCheck,
  Loader2,
  X,
  ChevronLeft,
  ChevronRight,
  UserPlus,
} from "lucide-react";

const LIMIT = 10;

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("cs-CZ").replace(/\. /g, ".");
}

// stejné chování jako na backendu (odebrání úvodních nul)
function normalizePharmacyCode(code) {
  if (code === undefined || code === null) return "";
  const s = String(code).trim();
  return s.replace(/^0+(?=\d)/, "");
}

function regionsArrayToText(regions) {
  const arr = Array.isArray(regions) ? regions : [];
  return arr.map(normalizePharmacyCode).filter(Boolean).join(", ");
}

function regionsTextToArray(text) {
  const raw = String(text || "");
  const parts = raw
    .split(",")
    .map((s) => normalizePharmacyCode(s))
    .map((s) => s.trim())
    .filter(Boolean);

  // unique
  return Array.from(new Set(parts));
}

export default function AdminUsersPage() {
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [page, setPage] = useState(1);

  const [total, setTotal] = useState(0);
  const [rows, setRows] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");

  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [newUser, setNewUser] = useState({
    username: "",
    email: "",
    password: "",
    pharmacyCode: "",
    firma: "",
    role: "user",
    fullName: "",
    regionsText: "", // UI field (string)
  });

  const [creating, setCreating] = useState(false);

  // debounce
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 400);
    return () => clearTimeout(t);
  }, [q]);

  // načtení seznamu
  useEffect(() => {
    (async () => {
      setLoading(true);
      setError("");

      try {
        const params = new URLSearchParams({
          q: debouncedQ,
          page,
          limit: LIMIT,
        }).toString();

        const res = await fetch(`/api/admin/users?${params}`, {
          method: "GET",
          credentials: "include",
        });

        if (!res.ok) throw new Error("Chyba při načítání listu");

        const json = await res.json();
        setRows(json?.users || []);
        setTotal(json?.total || 0);
      } catch (e) {
        setError("Nepodařilo se načíst seznam uživatelů.");
      } finally {
        setLoading(false);
      }
    })();
  }, [debouncedQ, page]);

  const pageCount = useMemo(() => Math.max(1, Math.ceil(total / LIMIT)), [total]);

  const openDetail = async (id) => {
    setDetailLoading(true);
    setError("");
    setOk("");

    try {
      const res = await fetch(`/api/admin/users/${id}`, {
        method: "GET",
        credentials: "include",
      });

      if (!res.ok) throw new Error("Chyba detailu");

      const json = await res.json();
      const u = json?.user || null;

      if (u) {
        setSelected({
          ...u,
          regionsText: regionsArrayToText(u.regions),
        });
      } else {
        setSelected(null);
      }
    } catch (err) {
      setError("Nepodařilo se načíst detail uživatele.");
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setSelected(null);
    setSaving(false);
    setDeleting(false);
    setOk("");
  };

  const saveAdminFields = async () => {
    if (!selected?._id) return;

    setSaving(true);
    setError("");
    setOk("");

    try {

      const payload = {
        role: String(selected.role ?? "").trim().toLowerCase(),
        pharmacyCode:
          selected.pharmacyCode === "" || selected.pharmacyCode === null
            ? ""
            : String(selected.pharmacyCode),
        firma: selected.firma ?? "",
        fullName: selected.fullName ?? "",
        // ✅ ber vždy z regionsText (UI), ať se změny fakt odešlou
        regions: regionsTextToArray(selected.regionsText ?? ""),
      };      
      const res = await fetch(`/api/admin/users/${selected._id}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        let msg = "Chyba při ukládání";
        try {
          const j = await res.json();
          if (j?.message) msg = j.message;
        } catch {}
        throw new Error(msg);
      }

      const json = await res.json();
      const updated = json.user;

      setRows((prev) =>
        prev.map((r) => (r._id === updated._id ? { ...r, ...updated } : r))
      );

      setSelected((prev) => ({
        ...(prev || {}),
        ...(updated || {}),
        regionsText: regionsArrayToText(updated?.regions),
      }));

      setOk("Změny uloženy.");
    } catch (err) {
      setError(err?.message || "Uložení se nezdařilo.");
    } finally {
      setSaving(false);
    }
  };

  const deleteUserById = async (id) => {
    if (!id) return;

    if (!window.confirm("Opravdu smazat tohoto uživatele?")) return;

    setDeleting(true);
    setError("");
    setOk("");

    try {
      const res = await fetch(`/api/admin/users/${id}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (!res.ok) throw new Error("Chyba při mazání");

      setRows((prev) => prev.filter((r) => r._id !== id));
      setTotal((t) => Math.max(0, t - 1));

      setOk("Uživatel byl smazán.");
      if (selected?._id === id) closeDetail();
    } catch (err) {
      setError("Smazání se nezdařilo.");
    } finally {
      setDeleting(false);
    }
  };

  const createUser = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError("");
    setOk("");

    try {
      const payload = {
        username: String(newUser.username ?? "").trim(),
        email: String(newUser.email ?? "").trim(),
        password: newUser.password,
        pharmacyCode: String(newUser.pharmacyCode ?? "").trim(),
        firma: String(newUser.firma ?? "").trim(),
        role: String(newUser.role ?? "").trim().toLowerCase(),
        fullName: String(newUser.fullName ?? "").trim(),
        regions: regionsTextToArray(newUser.regionsText ?? ""),       

      };

      const res = await fetch(`/api/admin/users`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        let msg = "Chyba při vytváření";
        try {
          const j = await res.json();
          if (j?.message) msg = j.message;
        } catch {}
        throw new Error(msg);
      }

      const json = await res.json();

      setRows((prev) => [json.user, ...prev]);
      setTotal((t) => t + 1);
      setOk("✅ Uživatel vytvořen.");

      setNewUser({
        username: "",
        email: "",
        password: "",
        pharmacyCode: "",
        firma: "",
        role: "user",
        fullName: "",
        regionsText: "",
      });
    } catch (err) {
      setError(err?.message || "Vytvoření se nezdařilo.");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <header className="flex items-center gap-3">
        <h1 className="text-2xl font-bold">Uživatelé</h1>
        <span className="ml-auto text-sm text-gray-500">
          Nalezeno: {total} • Stránka {page}/{pageCount}
        </span>
      </header>

      {/* CREATE USER FORM */}
      <form
        onSubmit={createUser}
        className="p-4 border rounded-xl bg-white shadow-sm space-y-3"
      >
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <UserPlus size={20} /> Nový uživatel
        </h2>

        <div className="grid grid-cols-2 gap-3">
          <input
            className="border p-2 rounded"
            placeholder="Username"
            required
            value={newUser.username}
            onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
          />
          <input
            className="border p-2 rounded"
            type="email"
            placeholder="Email"
            required
            value={newUser.email}
            onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <input
            className="border p-2 rounded"
            type="password"
            placeholder="Heslo"
            required
            value={newUser.password}
            onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
          />
          <input
            className="border p-2 rounded"
            placeholder="Kód pobočky"
            required
            value={newUser.pharmacyCode}
            onChange={(e) =>
              setNewUser({ ...newUser, pharmacyCode: e.target.value })
            }
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <input
            className="border p-2 rounded"
            placeholder="Firma"
            required
            value={newUser.firma}
            onChange={(e) => setNewUser({ ...newUser, firma: e.target.value })}
          />
          <select
            className="border p-2 rounded"
            value={newUser.role}
            onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
          >
            <option value="user">user</option>
            <option value="rm">rm</option>
            <option value="admin">admin</option>
          </select>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <input
            className="border p-2 rounded"
            placeholder="Celé jméno"
            value={newUser.fullName}
            onChange={(e) => setNewUser({ ...newUser, fullName: e.target.value })}
          />
          <input
            className="border p-2 rounded"
            type="text"
            placeholder="Regions pro RM (např. 64,66,67)"
            value={newUser.regionsText}
            onChange={(e) =>
              setNewUser({ ...newUser, regionsText: e.target.value })
            }
          />
        </div>

        <button
          type="submit"
          disabled={creating}
          className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 flex items-center gap-2"
        >
          {creating ? (
            "Vytvářím…"
          ) : (
            <>
              <UserPlus size={18} />
              Vytvořit
            </>
          )}
        </button>
      </form>

      {/* SEARCH */}
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-2 rounded-lg border px-3 py-2 bg-white w-full max-w-md">
          <Search size={18} className="opacity-60" />
          <input
            placeholder="Hledat podle username nebo emailu…"
            className="w-full outline-none"
            value={q}
            onChange={(e) => {
              setPage(1);
              setQ(e.target.value);
            }}
          />
        </div>
      </div>

      {error && <div className="p-3 rounded bg-red-50 text-red-700">{error}</div>}
      {ok && <div className="p-3 rounded bg-green-50 text-green-700">{ok}</div>}

      {/* TABLE */}
      <div className="overflow-x-auto rounded-xl border bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="[&>th]:text-left [&>th]:px-4 [&>th]:py-3 text-gray-600">
              <th>Uživatel</th>
              <th>Email</th>
              <th>Role</th>
              <th>Kód pobočky</th>
              <th>Vytvořen</th>
              <th className="w-40">Akce</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-gray-500">
                  <div className="inline-flex items-center gap-2">
                    <Loader2 className="animate-spin" size={18} />
                    Načítám…
                  </div>
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-gray-500">
                  Nic nenalezeno.
                </td>
              </tr>
            ) : (
              rows.map((u) => (
                <tr key={u._id} className="border-t hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium">{u.username}</td>
                  <td className="px-4 py-3">{u.email}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs ${
                        u.role === "admin"
                          ? "bg-indigo-50 text-indigo-700"
                          : "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {u.role === "admin" ? (
                        <ShieldCheck size={14} />
                      ) : (
                        <Shield size={14} />
                      )}
                      {u.role}
                    </span>
                  </td>
                  <td className="px-4 py-3">{u.pharmacyCode}</td>
                  <td className="px-4 py-3">{fmtDate(u.createdAt)}</td>

                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => openDetail(u._id)}
                        className="inline-flex items-center gap-1 rounded-md border px-2 py-1 hover:bg-gray-50"
                      >
                        <Pencil size={16} /> Upravit
                      </button>

                      <button
                        onClick={() => deleteUserById(u._id)}
                        className="inline-flex items-center gap-1 rounded-md border px-2 py-1 hover:bg-red-50 text-red-700 border-red-200"
                      >
                        <Trash2 size={16} /> Smazat
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* PAGINATION */}
      <div className="flex items-center justify-end gap-2">
        <button
          className="inline-flex items-center gap-1 rounded-md border px-3 py-1.5 disabled:opacity-50"
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page <= 1 || loading}
        >
          <ChevronLeft size={16} /> Předchozí
        </button>

        <span className="text-sm text-gray-600">
          {page} / {pageCount}
        </span>

        <button
          className="inline-flex items-center gap-1 rounded-md border px-3 py-1.5 disabled:opacity-50"
          onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
          disabled={page >= pageCount || loading}
        >
          Další <ChevronRight size={16} />
        </button>
      </div>

      {/* DETAIL MODAL */}
      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="w-full max-w-lg rounded-xl bg-white shadow-lg">
            <div className="flex items-center justify-between border-b px-4 py-3">
              <h3 className="text-lg font-semibold">Uživatel: {selected.username}</h3>
              <button onClick={closeDetail} className="p-1 hover:bg-gray-100 rounded">
                <X size={18} />
              </button>
            </div>

            <div className="p-4 space-y-3">
              {detailLoading ? (
                <div className="text-gray-500">Načítám detail…</div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Email</label>
                      <input
                        className="w-full border rounded px-3 py-2 bg-gray-100"
                        value={selected.email || ""}
                        readOnly
                      />
                    </div>

                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Username</label>
                      <input
                        className="w-full border rounded px-3 py-2 bg-gray-100"
                        value={selected.username || ""}
                        readOnly
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Role</label>
                      <select
                        className="w-full border rounded px-3 py-2"
                        value={selected.role}
                        onChange={(e) =>
                          setSelected((s) => ({ ...s, role: String(e.target.value ?? "") }))
                        }
                      >
                        <option value="user">user</option>
                        <option value="rm">rm</option>
                        <option value="admin">admin</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Kód pobočky</label>
                      <input
                        className="w-full border rounded px-3 py-2"
                        value={selected.pharmacyCode ?? ""}
                        onChange={(e) =>
                          setSelected((s) => ({
                            ...s,
                            pharmacyCode: e.target.value,
                          }))
                        }
                      />
                    </div>
                  </div>

                  {/* Firma + celé jméno */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Firma</label>
                      <input
                        className="w-full border rounded px-3 py-2"
                        value={selected.firma ?? ""}
                        onChange={(e) =>
                          setSelected((s) => ({ ...s, firma: e.target.value }))
                        }
                      />
                    </div>

                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Celé jméno</label>
                      <input
                        className="w-full border rounded px-3 py-2"
                        value={selected.fullName ?? ""}
                        onChange={(e) =>
                          setSelected((s) => ({
                            ...s,
                            fullName: e.target.value,
                          }))
                        }
                      />
                    </div>
                  </div>

                  {/* ✅ Regions pro RM */}
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      Regions (pobočky pro RM) – např. 64,66,67
                    </label>
                    <input
                      className="w-full border rounded px-3 py-2"
                      type="text"
                      value={selected.regionsText ?? ""}
                      onChange={(e) =>
                        setSelected((s) => ({ ...s, regionsText: e.target.value }))
                      }
                      placeholder="64,66,67"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Ukládá se jako pole. Úvodní nuly se automaticky odstraní.
                    </p>
                  </div>

                  {error && (
                    <div className="p-2 bg-red-50 text-red-700 rounded text-sm">{error}</div>
                  )}
                  {ok && (
                    <div className="p-2 bg-green-50 text-green-700 rounded text-sm">{ok}</div>
                  )}
                </>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 border-t px-4 py-3">
              <button className="rounded-md border px-4 py-2 hover:bg-gray-50" onClick={closeDetail}>
                Zavřít
              </button>

              <button
                className="rounded-md bg-blue-600 text-white px-4 py-2 hover:bg-blue-700 disabled:opacity-60"
                onClick={saveAdminFields}
                disabled={saving || detailLoading}
              >
                {saving ? "Ukládám…" : "Uložit změny"}
              </button>

              <button
                className="rounded-md bg-red-600 text-white px-4 py-2 hover:bg-red-700 disabled:opacity-60"
                onClick={() => deleteUserById(selected._id)}
                disabled={deleting || detailLoading}
              >
                {deleting ? "Mažu…" : "Smazat"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}